// Module 12 — phone-vs-tablet/laptop/desktop classification. Reuses the SAME signals the
// quality governor already reads (env-signals.ts: coarsePointer/touch/smallScreen, derived from
// `screen.width/height` — the physical short side, orientation-invariant) instead of UA-sniffing, which
// is fragile (iPadOS 13+ reports a desktop Safari UA by default) and would duplicate a device-detection
// heuristic this codebase already trusts for the exact same "how capable/how small is this device"
// question. Pure: no DOM/globals; app/index.ts binds the real signals.
//
// The screen-size heuristic alone has a real gap (adversarial review, 2026-07-06): a physical-size
// threshold can't cleanly separate large phones from small tablets in every case — a big phone with its
// OS "display size" set smaller-than-default reports a LARGER CSS-px screen (pushing it above the
// threshold, misclassifying a real phone as non-mobile), while some 7-8" Android tablets report a
// short side small enough to dip BELOW it (misclassifying a tablet as a phone). Where available,
// `uaMobile` (Chromium's User-Agent Client Hints `navigator.userAgentData.mobile`) is the browser/OS's
// OWN form-factor classification and is trusted as authoritative — it isn't affected by display-scale
// or physical screen size at all, and Android phones vs. tablets report it differently at the OS level.
// It's `null` on Firefox/Safari (no Client Hints), where the screen-size heuristic remains the fallback.
import type { EnvSignals } from "./env-signals";

/** The EnvSignals fields the phone classifier needs — accepted narrowly so a caller need not
 *  construct a full EnvSignals just to test this. */
export type MobileEnv = Pick<EnvSignals, "coarsePointer" | "touch" | "smallScreen" | "uaMobile">;

/** True only for a phone-class device — never a tablet, touch laptop, or desktop. */
export function isMobilePhone(env: MobileEnv): boolean {
  if (env.uaMobile !== null) return env.uaMobile && env.touch; // Chromium's own form-factor call
  return env.coarsePointer && env.touch && env.smallScreen; // Firefox/Safari fallback
}
